import { defineDspModule, type DspModuleInstance, type DspProcessBlock } from '../../dsp-module-contract/src/index.js';
import { SeedTree, type RandomStream } from '../../mod-035/src/index.js';

export const MAX_STOCHASTIC_STREAMS = 64;
export const STOCHASTIC_MODES = Object.freeze([
  'sample-and-hold',
  'slew',
  'random-walk',
  'correlated',
] as const);
export type StochasticMode = (typeof STOCHASTIC_MODES)[number];

export interface StochasticModulatorOptions {
  readonly sampleRate: number;
  readonly seed?: number;
  readonly mode?: StochasticMode;
  readonly rateHz?: number;
  readonly minimum?: number;
  readonly maximum?: number;
  readonly slewSeconds?: number;
  readonly stepSize?: number;
  readonly correlation?: number;
}

const UINT32_MAX = 0xffff_ffff;
const MODE_BY_INDEX: Readonly<Record<number, StochasticMode>> = Object.freeze({
  0: 'sample-and-hold',
  1: 'slew',
  2: 'random-walk',
  3: 'correlated',
});

const assertRange = (label: string, value: number, minimum: number, maximum: number): void => {
  if (!Number.isFinite(value) || value < minimum || value > maximum) {
    throw new RangeError(`${label} must be in [${minimum}, ${maximum}]`);
  }
};

const assertSeed = (seed: number): void => {
  if (!Number.isInteger(seed) || seed < 0 || seed > UINT32_MAX) {
    throw new RangeError('seed must be a uint32 integer');
  }
};

const isMode = (value: string): value is StochasticMode => (
  (STOCHASTIC_MODES as readonly string[]).includes(value)
);

/**
 * Seeded bounded control generator. Timing and PRNG state are retained across
 * calls, making one long render sample-identical to arbitrarily segmented
 * processing. The class owns no host, routing, UI, or output buffers.
 */
export class StochasticModulator {
  readonly sampleRate: number;

  private seed: number;
  private mode: StochasticMode;
  private rateHz: number;
  private minimum: number;
  private maximum: number;
  private slewSeconds: number;
  private stepSize: number;
  private correlation: number;
  private stream: RandomStream;
  private value = 0;
  private target = 0;
  private samplesUntilUpdate = 0;

  constructor(options: StochasticModulatorOptions) {
    assertRange('sampleRate', options.sampleRate, 1, 768_000);
    this.sampleRate = options.sampleRate;
    this.seed = options.seed ?? 0;
    this.mode = options.mode ?? 'sample-and-hold';
    this.rateHz = options.rateHz ?? 2;
    this.minimum = options.minimum ?? -1;
    this.maximum = options.maximum ?? 1;
    this.slewSeconds = options.slewSeconds ?? 0.1;
    this.stepSize = options.stepSize ?? 0.1;
    this.correlation = options.correlation ?? 0.8;
    this.validateConfiguration();
    this.stream = this.createStream();
    this.initializeState();
  }

  reset(): void {
    this.stream = this.createStream();
    this.initializeState();
  }

  setParameter(id: string, value: number): void {
    if (id === 'seed') {
      assertSeed(value);
      this.seed = value;
      this.reset();
      return;
    }
    if (id === 'mode') {
      if (!Number.isInteger(value) || MODE_BY_INDEX[value] === undefined) {
        throw new RangeError('mode must be an integer in [0, 3]');
      }
      this.mode = MODE_BY_INDEX[value]!;
      this.initializeState();
      return;
    }
    if (id === 'rate-hz') {
      assertRange(id, value, 0.001, 20_000);
      this.rateHz = value;
      return;
    }
    if (id === 'minimum') {
      assertRange(id, value, -1, 1);
      if (value > this.maximum) throw new RangeError('minimum must not exceed maximum');
      this.minimum = value;
      this.clampState();
      return;
    }
    if (id === 'maximum') {
      assertRange(id, value, -1, 1);
      if (value < this.minimum) throw new RangeError('maximum must not be below minimum');
      this.maximum = value;
      this.clampState();
      return;
    }
    if (id === 'slew-seconds') {
      assertRange(id, value, 0, 60);
      this.slewSeconds = value;
      return;
    }
    if (id === 'step-size') {
      assertRange(id, value, 0, 2);
      this.stepSize = value;
      return;
    }
    if (id === 'correlation') {
      assertRange(id, value, -1, 1);
      this.correlation = value;
      return;
    }
    throw new RangeError(`unknown parameter: ${id}`);
  }

  processInto(output: Float32Array): void {
    for (let index = 0; index < output.length; index += 1) {
      if (this.samplesUntilUpdate <= 0) {
        this.updateProcess();
        // A control stream cannot represent more than one new value per sample.
        this.samplesUntilUpdate += Math.max(1, this.sampleRate / this.rateHz);
      }

      if (this.mode === 'slew') this.advanceSlew();
      output[index] = this.value;
      this.samplesUntilUpdate -= 1;
    }
  }

  private createStream(): RandomStream {
    return new SeedTree(this.seed).stream('mod-016/output');
  }

  private initializeState(): void {
    this.value = (this.minimum + this.maximum) * 0.5;
    this.target = this.value;
    this.samplesUntilUpdate = 0;
  }

  private validateConfiguration(): void {
    assertSeed(this.seed);
    if (!isMode(this.mode)) throw new RangeError('mode is invalid');
    assertRange('rate-hz', this.rateHz, 0.001, 20_000);
    assertRange('minimum', this.minimum, -1, 1);
    assertRange('maximum', this.maximum, -1, 1);
    if (this.minimum > this.maximum) throw new RangeError('minimum must not exceed maximum');
    assertRange('slew-seconds', this.slewSeconds, 0, 60);
    assertRange('step-size', this.stepSize, 0, 2);
    assertRange('correlation', this.correlation, -1, 1);
  }

  private updateProcess(): void {
    if (this.mode === 'sample-and-hold') {
      this.value = this.stream.uniform(this.minimum, this.maximum);
      return;
    }
    if (this.mode === 'slew') {
      this.target = this.stream.uniform(this.minimum, this.maximum);
      if (this.slewSeconds === 0) this.value = this.target;
      return;
    }
    if (this.mode === 'random-walk') {
      const delta = this.stream.uniform(-this.stepSize, this.stepSize);
      this.value = Math.max(this.minimum, Math.min(this.maximum, this.value + delta));
      return;
    }
    this.value = this.stream.correlated(this.minimum, this.maximum, this.correlation);
  }

  private advanceSlew(): void {
    if (this.slewSeconds === 0) {
      this.value = this.target;
      return;
    }
    const maximumStep = (this.maximum - this.minimum) / (this.slewSeconds * this.sampleRate);
    const difference = this.target - this.value;
    if (Math.abs(difference) <= maximumStep) this.value = this.target;
    else this.value += Math.sign(difference) * maximumStep;
    this.clampState();
  }

  private clampState(): void {
    this.value = Math.max(this.minimum, Math.min(this.maximum, this.value));
    this.target = Math.max(this.minimum, Math.min(this.maximum, this.target));
  }
}

class StochasticModuleInstance implements DspModuleInstance {
  private readonly modulator: StochasticModulator;
  private readonly maxBlockSize: number;

  constructor(sampleRate: number, maxBlockSize: number) {
    this.maxBlockSize = maxBlockSize;
    this.modulator = new StochasticModulator({ sampleRate });
  }

  reset(): void { this.modulator.reset(); }
  setParameter(id: string, value: number): void { this.modulator.setParameter(id, value); }

  process(block: DspProcessBlock): void {
    if (!Number.isInteger(block.frames) || block.frames < 0 || block.frames > this.maxBlockSize) {
      throw new RangeError('frames exceeds the configured maximum block size');
    }
    const output = block.outputs.out;
    if (!output || output.length < block.frames) throw new RangeError('out buffer is missing or too short');
    this.modulator.processInto(output.subarray(0, block.frames));
  }
}

export const mod016 = defineDspModule({
  descriptor: {
    id: 'mir.mod-016',
    name: 'Random / stochastic modulator',
    version: '0.1.0',
    inputs: [],
    outputs: [{ id: 'out', kind: 'control', channels: 1 }],
    parameters: [
      { id: 'seed', defaultValue: 0, minValue: 0, maxValue: UINT32_MAX, unit: 'uint32' },
      { id: 'mode', defaultValue: 0, minValue: 0, maxValue: 3, unit: 'enum' },
      { id: 'rate-hz', defaultValue: 2, minValue: 0.001, maxValue: 20_000, unit: 'Hz' },
      { id: 'minimum', defaultValue: -1, minValue: -1, maxValue: 1, unit: 'ratio' },
      { id: 'maximum', defaultValue: 1, minValue: -1, maxValue: 1, unit: 'ratio' },
      { id: 'slew-seconds', defaultValue: 0.1, minValue: 0, maxValue: 60, unit: 's' },
      { id: 'step-size', defaultValue: 0.1, minValue: 0, maxValue: 2, unit: 'ratio' },
      { id: 'correlation', defaultValue: 0.8, minValue: -1, maxValue: 1, unit: 'ratio' },
    ],
  },
  createInstance(context) {
    if (!(context.sampleRate > 0) || !Number.isInteger(context.maxBlockSize) || context.maxBlockSize < 1) {
      throw new RangeError('invalid DSP instance context');
    }
    return new StochasticModuleInstance(context.sampleRate, context.maxBlockSize);
  },
});
